/**
 * The crash payout ceiling — the operator liability limit the backend clamps
 * every cash-out to.
 *
 * The crash multiplier is UNCAPPED, the payout is not: register_bet.go and
 * cashout_bet.go both bound the paid amount by the operator's LiabilityAmount.
 * The widget used to project `stake x multiplier` with no clamp, so a max-stake
 * player rode past the ceiling watching a figure the server would never pay
 * (a 10,000 stake against a 350,000 ceiling diverges from 35x up, and 2.57% of
 * production rounds crash beyond that).
 *
 * Only PROJECTIONS go through here — the live card amount, the Potential Win
 * pill, the auto-cash-out projection. Realized amounts (the win banner, the
 * optimistic cash-out figure) come from the backend response and are already
 * the truth; clamping those would corrupt a paid number.
 */

/**
 * Resolve `betConfig.maxPayout` into the DECIMAL display units the crash panel
 * works in, or null when no ceiling is configured.
 *
 * UNIT TRAP: `maxPayout` is stored in INTEGER wire units while the panel's
 * stakes and payouts are decimal — mixing them is the crypto-USD-1000x class of
 * bug, so the conversion is done here and nowhere else. Pass the currency-bound
 * `fromInteger`.
 *
 * `0` means NOT CONFIGURED, never "a ceiling of zero" — this mirrors the
 * backend's own `if maxPayout > 0` guard in CrashPotentialPayout, and matters
 * because CrashChipPanel's transient fallback betConfig carries maxPayout: 0
 * during a currency switch. Clamping to 0 there would blank every figure.
 */
export function resolvePayoutCeiling(
  maxPayout: number | undefined,
  fromInteger: (value: number) => number
): number | null {
  if (typeof maxPayout !== 'number' || !Number.isFinite(maxPayout) || maxPayout <= 0) {
    return null
  }
  return fromInteger(maxPayout)
}

/**
 * Freeze a projected payout at the ceiling. Above it the money stops while the
 * multiplier keeps climbing — which is exactly what the backend will pay.
 *
 * A null/NaN ceiling means "not configured": pass the value through untouched
 * rather than risk blanking a live money readout.
 */
export function clampToPayoutCeiling(value: number, ceiling: number | null): number {
  if (ceiling === null || !Number.isFinite(ceiling) || ceiling <= 0) return value
  return Math.min(value, ceiling)
}

/**
 * The riding projection the Potential Win pill renders: every active stake taken
 * to the live multiplier, each clamped to the ceiling INDIVIDUALLY, then summed —
 * plus the already-banked payout, which is never clamped.
 *
 * Extracted from the pill so the placement of the clamp is directly assertable.
 * The pill's rAF calls this, so a test on it fails if the aggregate-vs-per-bet
 * defect is ever reintroduced; asserting clampToPayoutCeiling alone proved only
 * that Math.min works, which was never in doubt, and let exactly that defect
 * through a fully green suite once already.
 *
 * `lockedPayout` is realized money the backend has already paid — clamping it
 * would under-report a completed payment.
 */
export function sumRidingProjection(
  lockedPayout: number,
  activeStakes: number[],
  multiplier: number,
  ceiling: number | null
): number {
  return (
    lockedPayout +
    activeStakes.reduce((sum, stake) => sum + clampToPayoutCeiling(stake * multiplier, ceiling), 0)
  )
}
